import React, { useState, useEffect, PropsWithChildren, type JSX } from 'react';
import ClientOnlyPortal from './ClientOnlyPortal';
import Close from '../../visuals/Icons/Old/Close';

interface IModal {
  isOpen?: boolean;
  externalToggle?: () => void;
  UNSAFE_className?: string;
  isClosable?: boolean;
  children: React.ReactNode;
  isOverlayClosable?: boolean;
  isSafelyClosable?: boolean;
  centered?: boolean;
  hasPadding?: boolean;
  title?: string;
}

const Modal = ({
  children,
  isOpen = false,
  externalToggle = () => undefined,
  UNSAFE_className,
  isClosable = true,
  isSafelyClosable = false, // (to optimize) quickfix to avoid misclick on the 3DS modal
  isOverlayClosable = false,
  centered = false,
  hasPadding = false,
  title,
}: PropsWithChildren<IModal>): JSX.Element | null => {
  const [isModalOpen, toggleIsModalOpen] = useState(isOpen);
  const [mouseDownTarget, setMouseDownTarget] = useState({});

  useEffect(() => {
    toggleIsModalOpen(isOpen);
  }, [isOpen]);

  const closeModal = (event) => {
    if (event.target === mouseDownTarget) {
      toggleIsModalOpen(false);
      externalToggle();
    }
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <ClientOnlyPortal selector="body">
      <div
        className="modal-overlay"
        onMouseDown={(event) => setMouseDownTarget(event.target)}
        onClick={
          isSafelyClosable
            ? undefined
            : isClosable || isOverlayClosable
            ? closeModal
            : undefined
        }
      >
        <div
          className={`modal-container ${hasPadding ? 'with-padding' : ''} ${
            UNSAFE_className || ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="modal-title-head">
            {title && <h3 className="modal-title">{title}</h3>}
            {isClosable && (
              <button
                className="modal-close"
                type="button"
                onClick={closeModal}
                data-testid="Modal-button-close-modal"
              >
                <Close width={11} height={11} />
              </button>
            )}
          </div>
          <div className="modal-content">{children}</div>
        </div>
      </div>
      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.65);
          display: flex;
          justify-content: center;
          align-items: ${centered ? 'center' : ''};
          z-index: var(--zindex-modal-backdrop);
          overflow-x: hidden;
          overflow-y: auto;
        }

        .modal-container {
          background: var(--white);
          border-radius: var(--rd-3);
          position: absolute;
          z-index: var(--zindex-modal);
        }

        .modal-container.with-padding {
          padding: var(--space-4);
        }

        @media (min-width: 800px) {
          .modal-container.with-padding {
            padding: var(--space-6);
          }
        }

        .with-padding .modal-content {
          padding: 20px 0 0;
        }

        .modal-title-head {
          display: flex;
          flex-direction: row;
        }

        .modal-title {
          font-family: var(--font-family-2);
          font-style: normal;
          font-weight: var(--font-weight-bold);
          font-size: var(--font-size-2);
          text-align: center;
          width: 100%;
        }

        .modal-close {
          position: absolute;
          top: 6px;
          right: 6px;
          padding: 10px;
          cursor: pointer;
          background: none;
          outline: none;
          border: none;
        }
      `}</style>
    </ClientOnlyPortal>
  );
};

export default Modal;
